import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Stack, useTheme } from "@mui/material";
import { useLoaderData, useNavigate } from "react-router-dom";
import { darken, lighten } from "@mui/material/styles";
import AddRounded from "@mui/icons-material/AddRounded";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import Bookings from "./Bookings";
import ClientDetails from "./ClientDetails";
import Analytics from "./Analytics";
import User from "../../../api/User";
import Booking, { BookingState } from "../../../api/Booking";
import Loyalty from "../../../api/Loyalty";
import { useCookies } from "react-cookie";
import UserProfileHeader, { ProfileAction } from "./UserProfileHeader";
import { FullScreenLoader } from "../../FullScreenLoader";
import TagFilterBar from "../../TagFilterBar";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import LoyaltySection from "./LoyaltySection";
import { ANALYTICS_ENABLED } from "../../../constants/features";

export const loader = async (request: any) => {
    const client_id = await request.params.client_id;
    return { client_id };
};

const BASE_TABS = ANALYTICS_ENABLED
    ? ["Bookings", "Details", "Analytics"]
    : ["Bookings", "Details"];

const UserInfo = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const hex = theme.palette.primary.main;

    const client_id = (useLoaderData() as any).client_id as string;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [user, setUser] = useState<User | undefined>();
    const [tab, setTab] = useState("Bookings");
    const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
    const [lastServiceKey, setLastServiceKey] = useState<string | undefined>();

    /* ── Scroll tracking: hero visibility ── */
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 24);
    }, []);

    /* ── Tab change: snap scroll to just past hero ── */
    useLayoutEffect(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        if (el.scrollTop > heroEl.offsetHeight - 24) {
            el.scrollTop = heroEl.offsetHeight - 24;
        }
    }, [tab]);

    useEffect(() => {
        if (!uuid || !brand_id) {
            navigate("/");
            return;
        }
        if (!client_id) {
            navigate("/users");
            return;
        }
        User.info(uuid, brand_id, client_id)
            .then((res) => {
                setUser(res);
            })
            .catch(console.error);
        Loyalty.getConfig(uuid, brand_id)
            .then((config) => {
                if (config) {
                    setLoyaltyEnabled(config.loyaltyReferral || config.loyaltySubscription);
                }
            })
            .catch(console.error);

        if (location_id) {
            Booking.listUser(uuid, brand_id, location_id, "history", client_id)
                .then((history) => {
                    // historyList already filters to "past or cancelled".
                    // The repeat shortcut should pull from past, non-cancelled
                    // bookings — matching the existing "Schedule Again" button
                    // on the Bookings tab (Bookings.tsx).
                    const completed = (history ?? []).find(
                        (b) =>
                            b.state !== BookingState.Cancel &&
                            b.service != null &&
                            b.service !== ""
                    );
                    setLastServiceKey(completed?.service);
                })
                .catch(console.error);
        }
    }, [uuid, brand_id, location_id, client_id, navigate]);

    const onBookAppointment = useCallback(() => {
        if (!user) return;
        sessionStorage.setItem("user_id", user.key);
        sessionStorage.removeItem("service_id");
        sessionStorage.removeItem("booking_id");
        sessionStorage.removeItem("isNewUser");
        sessionStorage.removeItem("slot_start");
        sessionStorage.removeItem("slot_end");
        sessionStorage.setItem("booking_user_preselected", "true");
        sessionStorage.setItem("booking_for_client_name", user.name);
        navigate("/calendar");
    }, [user, navigate]);

    const onRepeatLastBooking = useCallback(() => {
        if (!user || !lastServiceKey) return;
        sessionStorage.setItem("user_id", user.key);
        sessionStorage.setItem("service_id", lastServiceKey);
        sessionStorage.removeItem("booking_id");
        sessionStorage.removeItem("isNewUser");
        sessionStorage.removeItem("slot_start");
        sessionStorage.removeItem("slot_end");
        sessionStorage.setItem("booking_user_preselected", "true");
        sessionStorage.setItem("booking_for_client_name", user.name);
        navigate("/calendar");
    }, [user, lastServiceKey, navigate]);

    const onSaveNote = (note: string) =>
        setUser((x) => {
            if (x) x.note = note;
            return x;
        });

    const onBack = () => navigate("/users");

    const profileActions = useMemo<ProfileAction[]>(() => {
        const list: ProfileAction[] = [
            {
                key: "book",
                icon: <AddRounded sx={{ fontSize: 20 }} />,
                label: "Book appointment",
                onClick: onBookAppointment,
                variant: "primary",
            },
        ];
        if (lastServiceKey) {
            list.push({
                key: "repeat",
                icon: <ReplayRounded sx={{ fontSize: 20 }} />,
                label: "Repeat last booking",
                onClick: onRepeatLastBooking,
                variant: "secondary",
            });
        }
        return list;
    }, [onBookAppointment, onRepeatLastBooking, lastServiceKey]);

    if (user === undefined) return <FullScreenLoader />;

    return (
        <Stack
            ref={scrollRef}
            onScroll={handleScroll}
            direction="column"
            width="100%"
            flex={1}
            sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
        >
            {/* Hero */}
            <Stack
                ref={heroRef}
                width="100%"
                sx={{
                    background: `linear-gradient(180deg, ${lighten(hex, 0.08)} 0%, ${darken(hex, 0.08)} 100%)`,
                    pt: "16px",
                    pb: 5,
                    px: 2,
                    boxSizing: "border-box",
                    position: "relative",
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.04,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "128px 128px",
                        pointerEvents: "none",
                    },
                }}
            >
                {/* Back button */}
                <ButtonBase
                    onClick={onBack}
                    sx={{
                        alignSelf: "flex-start", mb: 2,
                        width: 36, height: 36, borderRadius: "50%",
                        bgcolor: "rgba(60,60,60,0.35)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                >
                    <ColoredIcon icon={BackIcon} color="white" size="28px" />
                </ButtonBase>

                {/* User profile card — identity + status + inline actions */}
                <UserProfileHeader
                    user={user}
                    expandablePhoto={true}
                    actions={profileActions}
                />
            </Stack>

            {/* Sticky tab bar — bottom-sheet starts directly after the hero */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    pt: scrolledPastHero ? 0.5 : 2,
                    pb: 0.5,
                    transition: "padding-top 0.25s ease",
                }}
            >
                <TagFilterBar
                    tags={loyaltyEnabled ? [...BASE_TABS, "Loyalty"] : BASE_TABS}
                    selectedTag={tab}
                    onSelectTag={setTab}
                />
            </Box>

            {/* Tab content */}
            <Box
                sx={{
                    "@keyframes fadeSlideIn": {
                        from: {
                            opacity: 0,
                            transform: "translateY(12px)",
                        },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeSlideIn 0.3s ease-out",
                }}
            >
                {tab === "Bookings" && <Bookings user_id={user.key} />}
                {tab === "Details" && (
                    <ClientDetails user={user} onSaveNote={onSaveNote} />
                )}
                {tab === "Analytics" && (
                    <Analytics user_id={user.key} />
                )}
                {tab === "Loyalty" && (
                    <LoyaltySection userKey={client_id} />
                )}
            </Box>
        </Stack>
    );
};

export default UserInfo;
